import { FC, useState, useMemo } from 'react';
import { Search, Plus } from 'lucide-react';
import { Chat, ChatTab } from '../../types';
import { IconButton } from '@new/common/ui/IconButton/IconButton';
import { ChatConversationItem } from '../ChatConversationItem/ChatConversationItem';
import { NewChatModal } from '../NewChatModal/NewChatModal';
import { getChatUserName } from '../../lib/helpers';

interface ChatSidebarProps {
  chats: Chat[];
  currentUserId: number;
  selectedChatId: number | undefined;
  unreadChatIds: Set<number>;
  onSelectChat: (chatId: number) => void;
}

export const ChatSidebar: FC<ChatSidebarProps> = ({
  chats,
  currentUserId,
  selectedChatId,
  unreadChatIds,
  onSelectChat,
}) => {
  const [activeTab] = useState<ChatTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);

  const filteredChats = useMemo(() => {
    let result = chats;

    if (activeTab === 'unread') {
      result = result.filter((chat) => unreadChatIds.has(chat.id));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((chat) =>
        getChatUserName(currentUserId, chat.users).toLowerCase().includes(query)
      );
    }

    return result.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return dateB - dateA;
    });
  }, [chats, activeTab, searchQuery, currentUserId, unreadChatIds]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-(--neutral-white-NW1000) rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between p-5">
        <div className="flex flex-col gap-1">
          <h4 className="[font-size:var(--fs-h4)]! font-bold text-(--neutral-black-NB1000)! m-0!">
            Messages
          </h4>
          <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB700)! m-0!">
            Quick, clear and concise communication.
          </p>
        </div>
        <IconButton
          icon={<Plus size={20} />}
          label="New chat"
          onClick={() => setIsNewChatModalOpen(true)}
        />
      </div>

      {/* Search */}
      <div className="px-5">
        <div className="h-12 flex items-center gap-2 rounded-lg p-4 border-[1.5px] border-[#E1E7EA]">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search messages..."
            className="flex-1 bg-transparent border-none outline-none [font-size:var(--fs-l4)]! text-(--neutral-black-NB1000) placeholder:text-(--neutral-white-NW600)"
          />
          <Search size={20} className="text-(--neutral-black-NB400) shrink-0 cursor-pointer" />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-2 w-full">
        {/* TODO: Implement when there is unread chat count on backend */}
        {/* <Tabs<ChatTab>
          tabs={chatTabs}
          value={activeTab}
          onValueChange={setActiveTab}
          triggerClassName="h-auto! px-4 py-1.5"
        /> */}
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {filteredChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB500) text-center m-0!">
              {searchQuery ? 'No conversations found' : 'No messages yet'}
            </p>
          </div>
        ) : (
          filteredChats.map((chat) => (
            <ChatConversationItem
              key={chat.id}
              chat={chat}
              currentUserId={currentUserId}
              isSelected={selectedChatId === chat.id}
              isUnread={unreadChatIds.has(chat.id)}
              onSelect={onSelectChat}
            />
          ))
        )}
      </div>

      {isNewChatModalOpen && (
        <NewChatModal isOpen={isNewChatModalOpen} onClose={() => setIsNewChatModalOpen(false)} />
      )}
    </div>
  );
};
